import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBoxes,
  FaShoppingCart,
  FaDollarSign,
  FaUsers,
  FaPlus,
  FaFileExport,
  FaChartLine,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import {
  fetchDashboardData,
  fetchRecentOrders,
  fetchTopProducts,
  fetchLowStockProducts,
  fetchRecentCustomers,
  exportOrdersCsv,
  fetchAdminCategories,
  createAdminCategory,
  updateAdminCategory,
  deleteAdminCategory,
  fetchRecentReviews,
  deleteAdminReview,
  approveReview,
  rejectReview,
} from "../adminApi";

const StatCard = ({ title, value, delta, icon: Icon, color, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left bg-white rounded-xl shadow-sm p-6 border-l-4 ${color} flex items-center gap-4 ${
      onClick
        ? "hover:shadow-md transition-shadow duration-150 cursor-pointer"
        : ""
    }`}
    type="button"
  >
    <div className="p-3 rounded-full bg-gray-50">
      <Icon className="h-6 w-6 text-gray-600" />
    </div>
    <div className="flex-1">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{title}</p>
      <div className="flex items-baseline gap-3">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        {typeof delta !== "undefined" && (
          <span
            className={`text-sm font-medium ${
              delta >= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {delta >= 0 ? `+${delta}%` : `${delta}%`}
          </span>
        )}
      </div>
    </div>
  </button>
);

const Badge = ({
  children,
  color = "bg-gray-100",
  textColor = "text-gray-700",
}) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color} ${textColor}`}
  >
    {children}
  </span>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    pendingOrders: 0,
    totalSales: 0,
    newCustomers: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryMessage, setCategoryMessage] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [approvingReviewId, setApprovingReviewId] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Try to fetch dashboard data to verify token
        await fetchDashboardData();
      } catch (err) {
        // If unauthorized, redirect to login
        if (
          err.message === "Unauthorized access" ||
          err.response?.status === 401 ||
          err.response?.status === 403
        ) {
          localStorage.removeItem("admin-token");
          navigate("/dashboard-admin/login");
          return;
        }
      }
    };
    checkAuth();
  }, [navigate]);
  const [orderCounts, setOrderCounts] = useState({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const [d, orders, tops, low, customers, reviewsData] =
          await Promise.all([
            fetchDashboardData(dateRange),
            fetchRecentOrders({ limit: 10, ...dateRange }),
            fetchTopProducts({ limit: 6, ...dateRange }),
            fetchLowStockProducts({ limit: 10, threshold: 5 }),
            fetchRecentCustomers({ limit: 6 }),
            fetchRecentReviews({ limit: 8 }),
          ]);
        if (!mounted) return;
        setStats({
          totalProducts: d.totalProducts ?? 0,
          pendingOrders: d.totalOrders ?? 0,
          totalSales: d.totalSales ?? 0,
          newCustomers: d.newCustomers ?? 0,
        });
        setRecentOrders(Array.isArray(orders) ? orders : orders?.orders || []);
        setTopProducts(tops || []);
        setLowStock(low || []);
        setRecentCustomers(customers || []);
        setRecentReviews(reviewsData?.reviews || []);
      } catch (err) {
        console.error("Dashboard load failed:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const loadOrderCounts = async () => {
      try {
        const statuses = [
          "pending",
          "processing",
          "shipped",
          "delivered",
          "cancelled",
        ];
        const results = await Promise.all(
          statuses.map(async (s) => {
            const resp = await fetchRecentOrders({ status: s });

            let totalCount = 0;
            if (Array.isArray(resp)) {
              totalCount = resp.length;
            } else {
              totalCount = resp.total ?? (resp.orders ? resp.orders.length : 0);
            }

            return {
              status: s,
              total: totalCount,
            };
          }),
        );

        if (!mounted) return;
        const map = {};
        results.forEach((r) => (map[r.status] = r.total || 0));
        setOrderCounts((prev) => ({ ...prev, ...map }));
      } catch (e) {
        console.error("Failed to load order counts:", e);
      }
    };

    load();
    loadOrderCounts();

    return () => {
      mounted = false;
    };
  }, [dateRange]);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      try {
        const categoryData = await fetchAdminCategories();
        if (!mounted) return;
        setCategories(Array.isArray(categoryData) ? categoryData : []);
      } catch (err) {
        if (!mounted) return;
        setCategoryError(err.message || "Failed to load categories.");
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, []);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setCategoryMessage("");
    setCategoryError("");

    const trimmedName = categoryName.trim();
    if (!trimmedName) {
      setCategoryError("Please enter a category name.");
      return;
    }

    setCreatingCategory(true);
    try {
      const created = await createAdminCategory({ name: trimmedName });
      setCategories((prev) => {
        const next = [...prev, created];
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });
      setCategoryName("");
      setCategoryMessage(`Category "${created.name}" added successfully.`);
    } catch (err) {
      setCategoryError(err.message || "Failed to create category.");
    } finally {
      setCreatingCategory(false);
    }
  };

  const startCategoryEdit = (category) => {
    setCategoryMessage("");
    setCategoryError("");
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name || "");
  };

  const cancelCategoryEdit = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
  };

  const handleCategoryUpdate = async (categoryId) => {
    const trimmedName = editingCategoryName.trim();
    if (!trimmedName) {
      setCategoryError("Please enter a category name.");
      return;
    }

    setCategoryMessage("");
    setCategoryError("");
    setUpdatingCategory(true);
    try {
      const updated = await updateAdminCategory(categoryId, {
        name: trimmedName,
      });

      setCategories((prev) => {
        const next = prev.map((category) =>
          category.id === categoryId ? updated : category,
        );
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });

      setEditingCategoryId(null);
      setEditingCategoryName("");
      setCategoryMessage(`Category "${updated.name}" updated successfully.`);
    } catch (err) {
      setCategoryError(err.message || "Failed to update category.");
    } finally {
      setUpdatingCategory(false);
    }
  };

  const handleCategoryDelete = async (categoryId, categoryNameToDelete) => {
    setCategoryMessage("");
    setCategoryError("");

    const confirmed = window.confirm(
      `Delete category "${categoryNameToDelete}"? This action cannot be undone.`,
    );

    if (!confirmed) return;

    setDeletingCategoryId(categoryId);
    try {
      await deleteAdminCategory(categoryId);
      setCategories((prev) =>
        prev.filter((category) => category.id !== categoryId),
      );
      if (editingCategoryId === categoryId) {
        setEditingCategoryId(null);
        setEditingCategoryName("");
      }
      setCategoryMessage(
        `Category "${categoryNameToDelete}" deleted successfully.`,
      );
    } catch (err) {
      setCategoryError(err.message || "Failed to delete category.");
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review? This action cannot be undone.")) {
      return;
    }

    setDeletingReviewId(reviewId);
    try {
      await deleteAdminReview(reviewId);
      setRecentReviews((prev) =>
        prev.filter((review) => review.review_id !== reviewId),
      );
    } catch (err) {
      console.error("Failed to delete review:", err);
      alert(err.message || "Failed to delete review.");
    } finally {
      setDeletingReviewId(null);
    }
  };

  const handleApproveReview = async (reviewId) => {
    setApprovingReviewId(reviewId);
    try {
      await approveReview(reviewId);
      setRecentReviews((prev) =>
        prev.map((r) =>
          r.review_id === reviewId
            ? {
                ...r,
                approved: true,
                flagged_for_review: false,
                rejection_reason: null,
              }
            : r,
        ),
      );
    } catch (err) {
      console.error("Failed to approve review:", err);
      alert(err.message || "Failed to approve review.");
    } finally {
      setApprovingReviewId(null);
    }
  };

  const handleRejectReview = async (reviewId) => {
    const reason = window.prompt(
      "Enter rejection reason (optional):",
      "Inappropriate content",
    );
    if (reason === null) return; // cancelled
    setApprovingReviewId(reviewId);
    try {
      await rejectReview(reviewId, reason || "Inappropriate content");
      setRecentReviews((prev) =>
        prev.map((r) =>
          r.review_id === reviewId
            ? {
                ...r,
                approved: false,
                flagged_for_review: false,
                rejection_reason: reason || "Inappropriate content",
              }
            : r,
        ),
      );
    } catch (err) {
      console.error("Failed to reject review:", err);
      alert(err.message || "Failed to reject review.");
    } finally {
      setApprovingReviewId(null);
    }
  };

  const handleExportOrders = async () => {
    setExporting(true);
    try {
      const blob = await exportOrdersCsv({
        from: dateRange.from || undefined,
        to: dateRange.to || undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${dateRange.from || "all"}_${dateRange.to || "now"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export orders:", error);
      alert(error.message || "Failed to export orders.");
    } finally {
      setExporting(false);
    }
  };

  const topProductsList = useMemo(
    () =>
      topProducts.map((p) => (
        <div key={p.product_id} className="flex items-center gap-3 py-2">
          <img
            src={
              p.thumbnail || `https://placehold.co/64x64/E0BBE4/FFFFFF?text=No`
            }
            alt={p.name_en}
            className="w-12 h-12 object-cover rounded"
          />
          <div className="flex-1">
            <div className="font-medium text-sm text-gray-900">
              {p.name_en || p.name_tr}
            </div>
            <div className="text-xs text-gray-500">Sold: {p.sold ?? 0}</div>
          </div>
          <div className="text-right text-sm font-semibold">
            TL {parseFloat(p.revenue ?? 0).toFixed(2)}
          </div>
        </div>
      )),
    [topProducts],
  );

  const formatMoney = (value) => `TL ${parseFloat(value ?? 0).toFixed(2)}`;

  const formatRelativeJoin = (createdAt) => {
    if (!createdAt) return "Unknown";
    const joinedDate = new Date(createdAt);
    const now = new Date();
    const diffDays = Math.floor((now - joinedDate) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return "Joined today";
    if (diffDays === 1) return "Joined yesterday";
    return `Joined ${diffDays} days ago`;
  };

  const initialsFromCustomer = (customer) => {
    const baseName = customer?.name || customer?.email || "U";
    return baseName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  };

  const newCustomers30d = useMemo(
    () => (recentCustomers || []).filter((c) => c.isNew30d).slice(0, 4),
    [recentCustomers],
  );

  // Calculate total actionable orders (Pending + Processing) for the KPI card
  const totalActionableOrders = useMemo(() => {
    return (orderCounts.pending ?? 0) + (orderCounts.processing ?? 0);
  }, [orderCounts]);

  if (loading) {
    return (
      <div className="text-center py-16 text-lg text-gray-600">
        Loading dashboard...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900">
            Admin Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Overview of store performance and quick actions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) =>
                setDateRange((s) => ({ ...s, from: e.target.value }))
              }
              className="border rounded px-3 py-2 text-sm"
            />
            <span className="text-sm text-gray-500">—</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) =>
                setDateRange((s) => ({ ...s, to: e.target.value }))
              }
              className="border rounded px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={() => setDateRange({ from: "", to: "" })}
            className="px-3 py-2 border rounded text-sm bg-white hover:bg-gray-50"
            title="Reset dates"
          >
            Reset
          </button>

          <button
            onClick={handleExportOrders}
            className="inline-flex items-center gap-2 bg-[#83375B] text-white px-4 py-2 rounded shadow"
            disabled={exporting}
          >
            <FaFileExport />
            {exporting ? "Exporting..." : "Export Orders"}
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total Products"
          value={stats.totalProducts.toLocaleString()}
          delta={stats.productsDelta}
          icon={FaBoxes}
          color="border-blue-500"
          onClick={() => navigate("/dashboard-admin/products")}
        />
        <StatCard
          title="Actionable Orders"
          value={totalActionableOrders}
          delta={stats.ordersDelta}
          icon={FaShoppingCart}
          color="border-yellow-500"
          // --- FIX APPLIED HERE ---
          // Use '?status=processing' to direct to the most relevant filter for action
          onClick={() => navigate("/dashboard-admin/orders?status=processing")}
          // ------------------------
        />
        <StatCard
          title="Total Sales (MTD)"
          value={`TL ${parseFloat(stats.totalSales ?? 0).toFixed(2)}`}
          delta={stats.salesDelta}
          icon={FaDollarSign}
          color="border-green-500"
          onClick={() => navigate("/dashboard-admin/orders")}
        />
        <StatCard
          title="New Customers (30d)"
          value={stats.newCustomers.toLocaleString()}
          delta={stats.customersDelta}
          icon={FaUsers}
          color="border-purple-500"
          onClick={() => navigate("/dashboard-admin/orders")}
        />
      </div>

      <div className="bg-white p-6 rounded-xl shadow mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">
              New Customers (Last 30 Days)
            </h3>
            <p className="text-sm text-gray-500">
              New signups with quick context for onboarding and retention
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {stats.newCustomers || 0} new
          </div>
        </div>

        {newCustomers30d.length === 0 ? (
          <div className="text-sm text-gray-400">
            No new customers in the last 30 days
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {newCustomers30d.map((customer) => (
              <div
                key={customer.user_id}
                className="rounded-lg border border-gray-200 p-4 bg-gradient-to-br from-white to-gray-50"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#F4E6EC] text-[#83375B] rounded-full flex items-center justify-center text-sm font-semibold">
                    {initialsFromCustomer(customer)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {customer.name || customer.email}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {customer.email}
                    </p>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <p>{formatRelativeJoin(customer.createdAt)}</p>
                  <p>Orders: {customer.orders_count ?? 0}</p>
                  <p>Spent: {formatMoney(customer.total_spent)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Orders and Sales */}
        <div className="lg:col-span-2 space-y-6">
          {/* Orders Overview */}
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Orders Overview</h3>
              <div className="text-sm text-gray-500">Quick actions</div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              {[
                "pending",
                "processing",
                "shipped",
                "delivered",
                "cancelled",
              ].map((s) => (
                <div
                  key={s}
                  className="bg-gray-50 p-3 rounded flex flex-col items-start"
                >
                  <div className="text-xs text-gray-400 capitalize">{s}</div>
                  <div className="text-xl font-bold">{orderCounts[s] ?? 0}</div>
                  <button
                    // --- CONFIRMED FIX ---
                    // Your existing code correctly passes the status parameter:
                    onClick={() =>
                      navigate(`/dashboard-admin/orders?status=${s}`)
                    }
                    // ---------------------
                    className="mt-2 text-xs text-[#83375B] hover:underline"
                  >
                    View {s}
                  </button>
                </div>
              ))}
            </div>

            <div className="h-40 flex items-center justify-center border rounded">
              <div className="text-sm text-gray-400">
                <FaChartLine className="inline-block mr-2 text-gray-400" />
                Sales / Orders mini chart (integrate Chart.js for real graphs)
              </div>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recent Orders</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/dashboard-admin/orders")}
                  className="text-sm text-[#83375B] hover:underline"
                >
                  View all
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2">Order</th>
                    <th className="py-2">Customer</th>
                    <th className="py-2">Items</th>
                    <th className="py-2">Total</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="py-6 text-center text-gray-400"
                      >
                        No recent orders
                      </td>
                    </tr>
                  ) : (
                    recentOrders.map((o) => (
                      <tr key={o.order_id} className="border-b">
                        <td className="py-3">
                          <button
                            onClick={() => navigate("/dashboard-admin/orders")}
                            className="text-sm font-medium text-[#83375B] hover:underline"
                          >
                            #{o.order_number || o.order_id}
                          </button>
                        </td>
                        <td className="py-3">
                          {o.customer_name || o.customer_email}
                        </td>
                        <td className="py-3">
                          {o.items_count ?? o.items?.length ?? 0}
                        </td>
                        <td className="py-3 font-semibold">
                          TL {parseFloat(o.total ?? 0).toFixed(2)}
                        </td>
                        <td className="py-3">
                          {o.status === "pending" ? (
                            <Badge
                              color="bg-yellow-100"
                              textColor="text-yellow-800"
                            >
                              Pending
                            </Badge>
                          ) : o.status === "shipped" ? (
                            <Badge
                              color="bg-green-100"
                              textColor="text-green-800"
                            >
                              Shipped
                            </Badge>
                          ) : o.status === "cancelled" ? (
                            <Badge color="bg-red-100" textColor="text-red-800">
                              Cancelled
                            </Badge>
                          ) : (
                            <Badge>{o.status || "—"}</Badge>
                          )}
                        </td>
                        <td className="py-3 text-sm text-gray-500">
                          {new Date(o.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right column: Top products, low stock, customers */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Top Selling Products</h3>
              <button
                onClick={() => navigate("/dashboard-admin/products")}
                className="text-sm text-[#83375B] hover:underline"
              >
                Manage
              </button>
            </div>
            <div>{topProductsList}</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Low Stock Alerts</h3>
              <div className="text-sm text-gray-500">
                {lowStock.length} items (&lt; 5)
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Showing products with stock strictly less than 5.
            </p>
            <div className="divide-y">
              {lowStock.length === 0 && (
                <div className="text-sm text-gray-400 py-4">
                  No products with stock less than 5
                </div>
              )}
              {lowStock.map((p) => (
                <div
                  key={p.product_id}
                  className="flex items-center justify-between py-3 cursor-pointer hover:bg-gray-50 rounded"
                  onClick={() =>
                    navigate(`/dashboard-admin/products/edit/${p.product_id}`)
                  }
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        p.thumbnail ||
                        `https://placehold.co/48x48/E0BBE4/FFFFFF?text=`
                      }
                      alt={p.name_en}
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div>
                      <div className="text-sm font-medium">{p.name_en}</div>
                      <div className="text-xs text-gray-500">
                        Stock: {p.stock_quantity}
                      </div>
                    </div>
                  </div>
                  <div>
                    <button
                      onClick={() =>
                        navigate(
                          `/dashboard-admin/products/edit/${p.product_id}`,
                        )
                      }
                      className="text-xs text-[#83375B] hover:underline"
                    >
                      Check
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Recent Customers</h3>
              <button
                onClick={() => navigate("/dashboard-admin/orders")}
                className="text-sm text-[#83375B] hover:underline"
              >
                All customers
              </button>
            </div>
            <div className="space-y-3">
              {recentCustomers.length === 0 && (
                <div className="text-sm text-gray-400">No recent customers</div>
              )}
              {recentCustomers.map((c) => (
                <div
                  key={c.user_id}
                  className="rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-sm text-gray-700 font-semibold">
                      {initialsFromCustomer(c)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {c.name || c.email}
                        </p>
                        {c.isNew30d && (
                          <Badge
                            color="bg-green-100"
                            textColor="text-green-700"
                          >
                            New
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {c.email}
                      </p>
                      {c.phone_number && (
                        <p className="text-xs text-gray-500">
                          {c.phone_number}
                        </p>
                      )}
                      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                        <p>
                          Joined: {new Date(c.createdAt).toLocaleDateString()}
                        </p>
                        <p>{formatRelativeJoin(c.createdAt)}</p>
                        <p>Orders: {c.orders_count ?? 0}</p>
                        <p>Spent: {formatMoney(c.total_spent)}</p>
                        <p className="col-span-2">
                          Last login:{" "}
                          {c.last_login
                            ? new Date(c.last_login).toLocaleString()
                            : "No login yet"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Categories</h3>
              <div className="text-sm text-gray-500">{categories.length}</div>
            </div>

            <form
              onSubmit={handleCategorySubmit}
              className="flex items-center gap-2 mb-3"
            >
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                className="flex-1 border rounded px-3 py-2 text-sm"
              />
              <button
                type="submit"
                disabled={creatingCategory}
                className="px-3 py-2 bg-[#83375B] text-white rounded text-sm disabled:opacity-60"
              >
                {creatingCategory ? "Adding..." : "Add"}
              </button>
            </form>

            {categoryError && (
              <p className="text-sm text-red-600 mb-2">{categoryError}</p>
            )}
            {categoryMessage && (
              <p className="text-sm text-green-600 mb-2">{categoryMessage}</p>
            )}

            <div className="max-h-44 overflow-auto border rounded">
              {categories.length === 0 ? (
                <div className="text-sm text-gray-500 p-3">
                  No categories yet.
                </div>
              ) : (
                <ul className="divide-y">
                  {categories.map((category) => (
                    <li
                      key={category.id}
                      className="px-3 py-2 text-sm text-gray-700"
                    >
                      {editingCategoryId === category.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingCategoryName}
                            onChange={(e) =>
                              setEditingCategoryName(e.target.value)
                            }
                            className="flex-1 border rounded px-2 py-1 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => handleCategoryUpdate(category.id)}
                            disabled={updatingCategory}
                            className="text-xs px-2 py-1 bg-green-600 text-white rounded disabled:opacity-60"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={cancelCategoryEdit}
                            disabled={updatingCategory}
                            className="text-xs px-2 py-1 border rounded disabled:opacity-60"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span>{category.name}</span>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => startCategoryEdit(category)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Edit category"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleCategoryDelete(category.id, category.name)
                              }
                              disabled={deletingCategoryId === category.id}
                              className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-60"
                              title="Delete category"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaPlus className="text-[#83375B]" />
              <div>
                <div className="text-sm font-medium">Quick Actions</div>
                <div className="text-xs text-gray-500">
                  Add product, manage discounts or view reports
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/dashboard-admin/products/new")}
                className="px-3 py-2 bg-green-500 text-white rounded"
              >
                Add Product
              </button>
              <button
                onClick={() => navigate("/dashboard-admin/orders")}
                className="px-3 py-2 border rounded"
              >
                Discounts
              </button>
              <button
                onClick={() => navigate("/dashboard-admin/reviews")}
                className="px-3 py-2 bg-orange-500 text-white rounded"
              >
                Review Moderation
              </button>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recent Reviews</h3>
              <div className="text-sm text-gray-500">
                {recentReviews.length} reviews
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Latest customer reviews from all products
            </p>

            {recentReviews.length === 0 ? (
              <div className="text-sm text-gray-400 py-4">No reviews yet</div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentReviews.map((review) => (
                  <div
                    key={review.review_id}
                    className="border rounded-lg p-3 hover:shadow-sm transition-shadow bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {review.User?.first_name || "Customer"}{" "}
                            {review.User?.last_name || ""}
                          </p>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) =>
                              star <= (review.rating || 0) ? (
                                <span
                                  key={star}
                                  className="text-yellow-500 text-xs"
                                >
                                  ★
                                </span>
                              ) : (
                                <span
                                  key={star}
                                  className="text-gray-300 text-xs"
                                >
                                  ★
                                </span>
                              ),
                            )}
                          </div>
                          {review.approved ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Approved
                            </span>
                          ) : review.rejection_reason ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Rejected
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                              Pending
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {review.Product?.name_en || "Product"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!review.approved && (
                          <button
                            onClick={() =>
                              handleApproveReview(review.review_id)
                            }
                            disabled={approvingReviewId === review.review_id}
                            className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
                            title="Approve review"
                          >
                            {approvingReviewId === review.review_id
                              ? "..."
                              : "Approve"}
                          </button>
                        )}
                        {review.approved && (
                          <button
                            onClick={() => handleRejectReview(review.review_id)}
                            disabled={approvingReviewId === review.review_id}
                            className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-60"
                            title="Reject review"
                          >
                            Reject
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteReview(review.review_id)}
                          disabled={deletingReviewId === review.review_id}
                          className="text-red-600 hover:text-red-800 p-1 disabled:opacity-60"
                          title="Delete review"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <p className="text-xs text-gray-700 leading-relaxed mb-2 line-clamp-2">
                      {review.comment || "No comment"}
                    </p>

                    {review.rejection_reason && (
                      <p className="text-xs text-red-600 mb-2 italic">
                        Rejected: {review.rejection_reason}
                      </p>
                    )}

                    {review.image_url && (
                      <div className="mb-2">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}${review.image_url}`}
                          alt="Review"
                          className="h-20 w-20 rounded object-cover border border-gray-200"
                        />
                      </div>
                    )}

                    <p className="text-xs text-gray-400">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        Pro tip: integrate charts (Chart.js, Recharts) and realtime sockets for
        live orders.
      </div>
    </div>
  );
}
